import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { ArrowRight, Mail, Lock, AlertCircle, Shield, ArrowLeft, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';

const LoginPage = () => {
  const { t } = useTranslation();
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const otpInputRef = useRef(null);
  const { register, handleSubmit, formState: { errors }, getValues } = useForm();

  // Focus OTP input when 2FA is required
  useEffect(() => {
    if (twoFactorRequired && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [twoFactorRequired]);

  const onSubmit = async (data) => {
    setLoading(true);
    setLoginError('');
    setShowResendButton(false);
    setResendSuccess(false);
    setUserEmail(data.email);

    try {
      // Call backend login API
      const result = await authService.login(data.email, data.password);

      // Check if 2FA is required
      if (result.twoFactorRequired) {
        setTwoFactorRequired(true);
        setTwoFactorToken(result.twoFactorToken);
        setUserEmail(result.email || data.email);
        setLoading(false);
        return;
      }

      // Complete login - redirect to portal
      redirectToPortal(result.user);

    } catch (error) {
      const message = error.message || 'Invalid email or password. Please try again.';
      setLoginError(message);
      setShowResendButton(message.toLowerCase().includes('verify your email'));
      setResendSuccess(false);
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const email = userEmail || document.querySelector('input[type="email"]')?.value;
    if (!email) return;
    setResendLoading(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/auth/resend-verification?email=${encodeURIComponent(email)}`, {
        method: 'POST',
      });
      setResendSuccess(true);
    } catch (err) {
      // Silently ignore — avoid leaking email existence
      setResendSuccess(true);
    } finally {
      setResendLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    if (twoFactorCode.length !== 6) {
      setLoginError(t('login.enterSixDigit'));
      return;
    }

    setLoading(true);
    setLoginError('');

    try {
      const formData = getValues();
      const result = await authService.login(
        formData.email,
        formData.password,
        twoFactorCode,
        twoFactorToken
      );

      if (result.twoFactorRequired) {
        setLoginError(t('login.invalidCode'));
        setLoading(false);
        return;
      }

      // Complete login - redirect to portal
      redirectToPortal(result.user);

    } catch (error) {
      setLoginError(error.message || t('login.invalidCode'));
      setLoading(false);
    }
  };

  const redirectToPortal = () => {
    const portalUrl = authService.getPortalUrl();
    const companyRole = localStorage.getItem('companyRole');
    const hasChatbot = localStorage.getItem('hasChatbotSubscription') === 'true';
    const hasCmms = localStorage.getItem('hasCmmsSubscription') === 'true';

    let path = '';
    if (companyRole === 'CHAT_AGENT') {
      path = '/portal/chatbot/inbox';
    } else if (companyRole === 'CHAT_SUPERVISOR') {
      path = '/portal/chatbot';
    } else if (!hasChatbot && hasCmms) {
      path = '/portal/cmms';
    }

    window.location.href = portalUrl + path;
  };

  const handleBackToLogin = () => {
    setTwoFactorRequired(false);
    setTwoFactorToken('');
    setTwoFactorCode('');
    setLoginError('');
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setTwoFactorCode(value);
  };

  return (
    <>
      <Helmet>
        <title>Login - Access Your Account | mwbTechs</title>
        <meta
          name="description"
          content="Login to your mwbTechs account. Access your CMMS, AI Chatbot, and more."
        />
      </Helmet>

      <div className="pt-20 min-h-screen bg-gray-50 flex items-center">
        <div className="container-custom py-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Welcome Back */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="hidden lg:block"
              >
                <h1 className="text-5xl font-bold text-gray-900 mb-6">
                  {t('login.welcomeBack')}
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  {t('login.signInDesc')}
                </p>

                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-3">{t('login.quickAccessTitle')}</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li>✓ {t('login.quickAccess1')}</li>
                      <li>✓ {t('login.quickAccess2')}</li>
                      <li>✓ {t('login.quickAccess3')}</li>
                      <li>✓ {t('login.quickAccess4')}</li>
                      <li>✓ {t('login.quickAccess5')}</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-primary text-white rounded-xl p-6">
                    <h3 className="font-bold mb-2">{t('login.newUser')}</h3>
                    <p className="text-white opacity-90 text-sm mb-4">
                      {t('login.startTrialPromo')}
                    </p>
                    <Link to="/signup" className="btn btn-white">
                      {t('login.createAccount')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </div>
                </div>
              </motion.div>

              {/* Right Side - Login Form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-10 border border-gray-200">
                  {!twoFactorRequired ? (
                    <>
                      {/* Normal Login Form */}
                      <div className="mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('login.signIn')}</h2>
                        <p className="text-gray-600">{t('login.signInSubtitle')}</p>
                      </div>

                      {loginError && (
                        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-semibold text-red-900">{t('login.loginFailed')}</div>
                            <div className="text-sm text-red-700">{loginError}</div>
                            {showResendButton && (
                              <div className="mt-3">
                                {resendSuccess ? (
                                  <div className="flex items-center gap-2 text-green-700 text-sm">
                                    <CheckCircle className="w-4 h-4" />
                                    {t('login.verificationEmailSent')}
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={handleResendVerification}
                                    disabled={resendLoading}
                                    className="text-sm font-medium text-red-800 underline hover:text-red-900 disabled:opacity-50"
                                  >
                                    {resendLoading ? t('login.sending') : t('login.resendVerification')}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('login.emailAddress')}
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="email"
                              {...register('email', {
                                required: t('login.emailRequired'),
                                pattern: {
                                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                  message: t('login.invalidEmail')
                                }
                              })}
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              placeholder="john@company.com"
                            />
                          </div>
                          {errors.email && (
                            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                          )}
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {t('login.password')}
                            </label>
                            <Link
                              to="/forgot-password"
                              className="text-sm text-primary-600 hover:underline"
                            >
                              {t('login.forgotPassword')}
                            </Link>
                          </div>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="password"
                              {...register('password', { required: t('login.passwordRequired') })}
                              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              placeholder="••••••••"
                            />
                          </div>
                          {errors.password && (
                            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                          )}
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            {...register('remember')}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label className="ml-2 text-sm text-gray-600">
                            {t('login.rememberMe')}
                          </label>
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full btn btn-primary text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <>
                              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                              {t('login.signingIn')}
                            </>
                          ) : (
                            <>
                              {t('login.signIn')}
                              <ArrowRight className="w-5 h-5 ml-2" />
                            </>
                          )}
                        </button>
                      </form>

                      <div className="mt-6 text-center text-sm text-gray-600">
                        {t('login.noAccount')}{' '}
                        <Link to="/signup" className="text-primary-600 hover:underline font-medium">
                          {t('login.startFreeTrial')}
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Two-Factor Authentication Form */}
                      <div className="mb-8">
                        <button
                          onClick={handleBackToLogin}
                          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                        >
                          <ArrowLeft className="w-4 h-4 mr-1" />
                          {t('login.backToLogin')}
                        </button>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                            <Shield className="w-6 h-6 text-primary-600" />
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold text-gray-900">{t('login.twoFactor')}</h2>
                            <p className="text-gray-600 text-sm">{t('login.twoFactorDesc')}</p>
                          </div>
                        </div>
                        <p className="text-gray-500 text-sm">
                          {t('login.twoFactorHint')} <strong>{userEmail}</strong>
                        </p>
                      </div>

                      {loginError && (
                        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-semibold text-red-900">{t('login.verificationFailed')}</div>
                            <div className="text-sm text-red-700">{loginError}</div>
                          </div>
                        </div>
                      )}

                      <form onSubmit={handleTwoFactorSubmit} className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('login.verificationCode')}
                          </label>
                          <input
                            ref={otpInputRef}
                            type="text"
                            value={twoFactorCode}
                            onChange={handleOtpChange}
                            className="w-full px-4 py-4 text-center text-3xl font-mono tracking-[0.5em] border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="000000"
                            maxLength={6}
                            autoComplete="one-time-code"
                          />
                          <p className="mt-2 text-sm text-gray-500">
                            {t('login.authenticatorHint')}
                          </p>
                        </div>

                        <button
                          type="submit"
                          disabled={loading || twoFactorCode.length !== 6}
                          className="w-full btn btn-primary text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <>
                              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                              {t('login.verifying')}
                            </>
                          ) : (
                            <>
                              {t('login.verifySignIn')}
                              <ArrowRight className="w-5 h-5 ml-2" />
                            </>
                          )}
                        </button>
                      </form>

                      <div className="mt-6 text-center">
                        <p className="text-sm text-gray-600 mb-2">
                          {t('login.troubleAuth')}
                        </p>
                        <Link to="/help" className="text-primary-600 hover:underline text-sm font-medium">
                          {t('login.contactSupport')}
                        </Link>
                      </div>
                    </>
                  )}
                </div>

                {/* Mobile CTA */}
                <div className="lg:hidden mt-6 bg-gradient-primary text-white rounded-xl p-6">
                  <h3 className="font-bold mb-2">{t('login.newUser')}</h3>
                  <p className="text-white opacity-90 text-sm mb-4">
                    {t('login.startTrialPromo')}
                  </p>
                  <Link to="/signup" className="btn btn-white">
                    {t('login.createAccount')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
